/**
 * Auth gate: probes `/api/v1/me` once on mount. If the response is 200
 * the user has a valid session cookie and the children render; if it's
 * 401 we redirect to `/login?next=...` so the user can authenticate and
 * come back to the same page.
 *
 * Anything other than 200/401 (5xx, network error) is shown as a fatal
 * error — nothing else in the app will work without the API.
 */
import i18n from "i18next";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation } from "react-router-dom";
import { useSession } from "../state/session";
import { SessionExpiringBanner } from "./SessionExpiringBanner";

type Status = "checking" | "authed" | "unauthed" | "error";

export function AuthGate({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>("checking");
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const loc = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    let cancelled = false;
    fetch("/api/v1/me", { credentials: "include" })
      .then(async (r) => {
        if (cancelled) return;
        if (r.status === 200) {
          setStatus("authed");
          // Hydrate de session-store ZODRA we authed zijn, op
          // élke route — niet alleen op de Editor. Anders zien
          // niet-Editor-routes (bv. /settings) een lege me/
          // workspaceId/branchId na refresh, en gating op
          // `isAdmin = me?.workspaces.find(...)` valt door =>
          // admin-only-tabs verdwijnen tot je via Editor passeert.
          if (useSession.getState().me === null) {
            void useSession.getState().hydrate();
          }
        } else if (r.status === 401) {
          setStatus("unauthed");
        } else {
          setErrMsg(i18n.t("auth.gate.unexpectedStatus", { status: r.status }));
          setStatus("error");
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setErrMsg(e instanceof Error ? e.message : i18n.t("auth.gate.networkError"));
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (status === "checking") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100">
        <p className="text-slate-500 text-sm">{t("auth.gate.authenticating")}</p>
      </div>
    );
  }

  if (status === "unauthed") {
    const next = encodeURIComponent(loc.pathname + loc.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  if (status === "error") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 px-4">
        <div className="max-w-md rounded border border-rose-300 bg-white p-6 shadow">
          <h1 className="mb-1 font-semibold text-rose-700">{t("auth.gate.apiFailed")}</h1>
          <p className="text-slate-700 text-sm">{errMsg}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <SessionExpiringBanner />
      {children}
    </>
  );
}
